import { format } from 'date-fns';
import {
  Bot,
  FileText,
  FilePlus,
  FolderOpen,
  Link,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { AgentLog } from '../../types';
import clsx from 'clsx';

interface LogItemProps {
  log: AgentLog;
  onClick?: () => void;
}

const operationIcons = {
  read_file: FileText,
  write_file: FilePlus,
  list_directory: FolderOpen,
  get_file_url: Link,
};

const operationLabels = {
  read_file: 'Read File',
  write_file: 'Write File',
  list_directory: 'List Directory',
  get_file_url: 'Get File URL',
};

export function LogItem({ log, onClick }: LogItemProps) {
  const Icon = operationIcons[log.operation] || FileText;
  const label = operationLabels[log.operation] || log.operation;
  const timestamp = format(new Date(log.timestamp), 'HH:mm:ss');
  const date = format(new Date(log.timestamp), 'MMM d, yyyy');

  return (
    <div
      className="px-4 py-3 border-b hover:bg-gray-50 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Bot className="w-5 h-5 text-purple-600" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">
              {log.agentId}
            </span>
            {log.success ? (
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            ) : (
              <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            )}
          </div>

          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            <span
              className={clsx(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                log.operation === 'write_file'
                  ? 'bg-green-100 text-green-700'
                  : log.operation === 'read_file'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-700'
              )}
            >
              <Icon className="w-3 h-3" />
              {label}
            </span>
            <span className="truncate">{log.workspace}</span>
          </div>

          <p className="text-sm text-gray-600 mt-1 truncate font-mono">
            {log.path}
          </p>
        </div>

        <div className="text-right text-xs text-gray-500 flex-shrink-0">
          <div>{timestamp}</div>
          <div>{date}</div>
          {log.duration > 0 && <div className="mt-1">{log.duration}ms</div>}
        </div>
      </div>
    </div>
  );
}
